import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Email,
  Phone,
  Edit,
  Archive,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { useClients, useClientStats, useDeleteClient } from '../../hooks/useClients';
import CreateClientModal from '../../components/clients/CreateClientModal';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { COLORS } from '@elevair/shared';

type StatusFilter = 'all' | 'active' | 'inactive' | 'lead' | 'archived';

export default function ClientsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<{ id: string; name: string } | null>(null);

  // Queries
  const { data, isLoading, error } = useClients({
    page: page + 1,
    limit: rowsPerPage,
    search: search || undefined,
    status,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { data: stats } = useClientStats();
  const deleteClient = useDeleteClient();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, clientId: string) => {
    setMenuAnchor(event.currentTarget);
    setSelectedClientId(clientId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedClientId(null);
  };

  const handleView = () => {
    if (selectedClientId) {
      navigate(`/clients/${selectedClientId}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedClientId) {
      navigate(`/clients/${selectedClientId}?edit=true`);
    }
    handleMenuClose();
  };

  const handleArchive = async () => {
    if (selectedClientId) {
      await deleteClient.mutateAsync({ id: selectedClientId, permanent: false });
    }
    handleMenuClose();
  };

  const handleOpenDeleteDialog = () => {
    if (selectedClientId) {
      const client = data?.clients.find(c => c._id === selectedClientId);
      if (client) {
        setClientToDelete({ id: client._id, name: client.fullName });
        setDeleteDialogOpen(true);
      }
    }
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (clientToDelete) {
      await deleteClient.mutateAsync({ id: clientToDelete.id, permanent: true });
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setClientToDelete(null);
  };

  const statusColors: Record<string, string> = {
    active: '#10B981',
    inactive: '#6B7280',
    lead: '#F59E0B',
    archived: '#EF4444',
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Clients
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats?.total || 0} total clients - {stats?.active || 0} active
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateModalOpen(true)}
        >
          Add Client
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: 'Active', value: stats?.active || 0, color: '#10B981', filter: 'active' },
          { label: 'Leads', value: stats?.leads || 0, color: '#F59E0B', filter: 'lead' },
          { label: 'Inactive', value: stats?.inactive || 0, color: '#6B7280', filter: 'inactive' },
          { label: 'Archived', value: stats?.archived || 0, color: '#EF4444', filter: 'archived' },
        ].map((stat) => (
          <Card
            key={stat.label}
            sx={{
              px: 3,
              py: 2,
              minWidth: 120,
              cursor: 'pointer',
              border: status === stat.filter ? `2px solid ${stat.color}` : '2px solid transparent',
              '&:hover': {
                borderColor: stat.color,
              },
            }}
            onClick={() => setStatus(status === stat.filter ? 'all' : stat.filter as StatusFilter)}
          >
            <Typography variant="h5" fontWeight={600}>
              {stat.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            placeholder="Search clients..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="lead">Leads</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load clients. Please try again.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {search ? 'No clients match your search' : 'No clients yet. Add your first client!'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.clients.map((client) => (
                    <TableRow
                      key={client._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/clients/${client._id}`)}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            src={client.avatarUrl}
                            sx={{
                              bgcolor: client.avatarColor || COLORS.primary,
                              width: 40,
                              height: 40,
                            }}
                          >
                            {client.initials}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={500}>
                              {client.fullName}
                            </Typography>
                            {client.jobTitle && (
                              <Typography variant="body2" color="text.secondary">
                                {client.jobTitle}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Email fontSize="small" color="action" />
                          <Typography variant="body2">{client.contact.email}</Typography>
                        </Box>
                        {client.contact.phone && (
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <Phone fontSize="small" color="action" />
                            <Typography variant="body2">{client.contact.phone}</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography>{client.company || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={client.status}
                          size="small"
                          sx={{
                            bgcolor: `${statusColors[client.status]}20`,
                            color: statusColors[client.status],
                            fontWeight: 500,
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {client.tags.slice(0, 3).map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {client.tags.length > 3 && (
                            <Chip
                              label={`+${client.tags.length - 3}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, client._id);
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={data?.pagination.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleArchive}>
          <Archive fontSize="small" sx={{ mr: 1 }} />
          Archive
        </MenuItem>
        <MenuItem onClick={handleOpenDeleteDialog} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Modal */}
      <CreateClientModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Client"
        message={
          <>
            Are you sure you want to permanently delete <strong>{clientToDelete?.name}</strong>?
            <br /><br />
            This will also delete all associated:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Bookings and appointments</li>
              <li>Invoices and payments</li>
              <li>Contracts and documents</li>
              <li>Notes and communications</li>
              <li>Tasks and time entries</li>
            </ul>
            <strong>This action cannot be undone.</strong>
          </>
        }
        confirmText="Delete Client"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteDialog}
        isLoading={deleteClient.isPending}
      />
    </Box>
  );
}
